<template>
  <div class="company-employees-manager">
    <div class="header-section">
      <h3 class="title">עובדי החברה</h3>
      <button @click="addNewEmployee" class="button is-primary is-small">
        <span class="icon">
          <i class="fas fa-plus"></i>
        </span>
        <span>הוסף עובד חדש</span>
      </button>
    </div>

    <div v-if="loading" class="has-text-centered">
      <div class="loader"></div>
      <p>טוען עובדים...</p>
    </div>

    <div v-else-if="employees.length === 0" class="has-text-centered">
      <p class="has-text-grey">אין עובדים רשומים לחברה זו</p>
    </div>

    <div v-else class="employees-table">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>שם פרטי</th>
            <th>שם משפחה</th>
            <th>אימייל</th>
            <th>טלפון</th>
            <th>תפקיד</th>
            <th>תאריך הצטרפות</th>
            <th>פעולות</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employee in employees" :key="employee.id">
            <td>{{ employee.user ? employee.user.first_name : 'לא זמין' }}</td>
            <td>{{ employee.user ? employee.user.last_name : 'לא זמין' }}</td>
            <td>{{ employee.user ? employee.user.email : 'לא זמין' }}</td>
            <td>{{ employee.user ? (employee.user.phone_number || 'לא צוין') : 'לא זמין' }}</td>
            <td>{{ employee.title || 'לא צוין' }}</td>
            <td>{{ formatDate(employee.created_at) }}</td>
            <td>
              <div class="buttons are-small">
                <button
                  @click="editEmployee(employee)"
                  class="button is-info is-small"
                  title="ערוך עובד"
                  :disabled="!employee.user"
                >
                  <span class="icon">
                    <i class="fas fa-edit"></i>
                  </span>
                  <span>ערוך</span>
                </button>
                <button
                  @click="resetPassword(employee)"
                  class="button is-warning is-small"
                  title="איפוס סיסמא"
                  :disabled="!employee.user"
                >
                  <span class="icon">
                    <i class="fas fa-key"></i>
                  </span>
                  <span>איפוס סיסמא</span>
                </button>
                <button
                  @click="deleteEmployee(employee)"
                  class="button is-danger is-small"
                  title="מחק עובד"
                >
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>מחק</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Reset Password Modal -->
    <div class="modal" :class="{ 'is-active': showPasswordModal }">
      <div class="modal-background" @click="closePasswordModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">איפוס סיסמא</p>
          <button class="delete" @click="closePasswordModal"></button>
        </header>
        <section class="modal-card-body">
          <p class="mb-4">
            האם אתה בטוח שברצונך לאפס את הסיסמא עבור
            <strong>{{ passwordResetEmployee && passwordResetEmployee.user ? passwordResetEmployee.user.first_name : '' }} {{ passwordResetEmployee && passwordResetEmployee.user ? passwordResetEmployee.user.last_name : '' }}</strong>?
          </p>
          <div class="field">
            <label class="label">סיסמא חדשה</label>
            <div class="control">
              <input
                v-model="newPassword"
                class="input"
                type="password"
                placeholder="סיסמא חדשה"
                :class="{ 'is-danger': errors.newPassword }"
              >
            </div>
            <p v-if="errors.newPassword" class="help is-danger">{{ errors.newPassword }}</p>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button
            @click="confirmPasswordReset"
            class="button is-warning"
            :class="{ 'is-loading': resettingPassword }"
            :disabled="resettingPassword"
          >
            אפס סיסמא
          </button>
          <button @click="closePasswordModal" class="button">ביטול</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CompanyEmployeesManager',
  props: ['payload'],
  data () {
    return {
      loading: false,
      resettingPassword: false,
      employees: [],
      showPasswordModal: false,
      passwordResetEmployee: null,
      newPassword: '',
      errors: {}
    }
  },
  created () {
    this.loadEmployees()
  },
  methods: {
    async loadEmployees () {
      this.loading = true
      try {
        const response = await this.$network_utils.getCompanyEmployees({
          company_id: this.payload.company_id
        })
        // The network_utils.getCompanyEmployees already extracts the data array
        this.employees = response || []
      } catch (error) {
        console.error('Error loading employees:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת עובדים' })
      } finally {
        this.loading = false
      }
    },

    addNewEmployee () {
      this.$store.commit('modals/create', {
        designator: 'add-company-employee',
        component_name: 'CompanyEmployeesForm',
        modal_title: 'הוסף עובד חדש',
        modal_width: 600,
        company_id: this.payload.company_id,
        employee: null,
        onSave: this.loadEmployees
      })
    },

    editEmployee (employee) {
      this.$store.commit('modals/create', {
        designator: 'edit-company-employee',
        component_name: 'CompanyEmployeesForm',
        modal_title: 'ערוך עובד - ' + (employee.user ? employee.user.first_name + ' ' + employee.user.last_name : 'לא זמין'),
        modal_width: 600,
        company_id: this.payload.company_id,
        employee: employee,
        onSave: this.loadEmployees
      })
    },

    resetPassword (employee) {
      this.passwordResetEmployee = employee
      this.newPassword = ''
      this.errors = {}
      this.showPasswordModal = true
    },

    closePasswordModal () {
      this.showPasswordModal = false
      this.passwordResetEmployee = null
      this.newPassword = ''
      this.errors = {}
    },

    async confirmPasswordReset () {
      if (!this.newPassword || !this.newPassword.trim()) {
        this.errors.newPassword = 'סיסמא חדשה חובה'
        return
      }

      this.resettingPassword = true
      try {
        await this.$network_utils.resetPassword({
          id: this.passwordResetEmployee.user.id,
          password: this.newPassword
        })

        this.$utils.emit('show_toast', { text: 'סיסמא אופסה בהצלחה' })
        this.closePasswordModal()
      } catch (error) {
        console.error('Error resetting password:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה באיפוס סיסמא' })
      } finally {
        this.resettingPassword = false
      }
    },

    async deleteEmployee (employee) {
      if (!confirm(`האם אתה בטוח שברצונך למחוק את ${employee.user.first_name} ${employee.user.last_name}?`)) {
        return
      }

      try {
        await this.$network_utils.caller.request('delete', `/company_employees/${employee.id}`, 'admin_headers')
        this.$utils.emit('show_toast', { text: 'עובד נמחק בהצלחה' })
        await this.loadEmployees()
      } catch (error) {
        console.error('Error deleting employee:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה במחיקת עובד' })
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'לא זמין'
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL')
    }
  }
}
</script>

<style scoped>
.company-employees-manager {
  padding: 1rem;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.title {
  margin: 0;
  color: #363636;
}

.employees-table {
  margin-top: 1rem;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.buttons {
  gap: 0.25rem;
}

.modal-card {
  width: 90%;
  max-width: 600px;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .employees-table {
    overflow-x: auto;
  }

  .table {
    min-width: 600px;
  }
}
</style>
